import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import FormInputError from "components/Form/Input/Error";
import FormInputLabel from "components/Form/Input/Label";
import {
  STYLE_INPUT,
  STYLE_INPUT_MARGIN_TOP,
} from "components/Form/Input/consts";
import {
  isCompleteSortCode,
  SORT_CODE_INVALID_MESSAGE,
} from "components/Form/Input/sortCodeValidation";

const SORT_CODE_MAX_DIGITS = 6;

const formatSortCodeValue = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, SORT_CODE_MAX_DIGITS);
  if (!digits.length) return "";
  const a = digits.slice(0, 2);
  const b = digits.length > 2 ? digits.slice(2, 4) : "";
  const c = digits.length > 4 ? digits.slice(4, 6) : "";
  return [a, b, c].filter(Boolean).join("-");
};

type FormInputSortCodeProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  id: string;
  label?: string;
  disabled?: boolean;
};

export default function FormInputSortCode<TFieldValues extends FieldValues>({
  control,
  name,
  id,
  label,
  disabled = false,
}: FormInputSortCodeProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (v) => {
          const s = String(v ?? "").trim();
          if (!s) return true;
          return isCompleteSortCode(s) || SORT_CODE_INVALID_MESSAGE;
        },
      }}
      render={({ field, fieldState }) => (
        <div>
          {label ? <FormInputLabel id={id} label={label} /> : null}
          <div className={STYLE_INPUT_MARGIN_TOP}>
            <input
              id={id}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="00-00-00"
              disabled={disabled}
              className={STYLE_INPUT}
              value={formatSortCodeValue(String(field.value ?? ""))}
              onChange={(e) => {
                field.onChange(formatSortCodeValue(e.target.value));
              }}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            />
            <FormInputError error={fieldState.error?.message} />
          </div>
        </div>
      )}
    />
  );
}
